import { useEffect, useMemo, useState } from 'react'
import { useGetDocumentRowValues } from '@/service-library/hooks/document-row-values'
import { useGetDocumentRows } from '@/service-library/hooks/document-rows'
import { useGetDocuments } from '@/service-library/hooks/documents'
import {
  APPROVALS_PAGE_SIZE,
  WAITING_FILL_MAX_PAGES,
} from '@/service-library/hooks/my-approvals'
import { WorkflowState } from '@/types/workflows'
import { getBaseGrid, getFieldsFromProject } from '@/utils/field'
import {
  INVOICE_TOTAL_INFO_TYPE_ID,
  INVOICE_VENDOR_INFO_TYPE_ID,
} from '@/utils/info-types'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'

export type ApprovalRowDetail = {
  name: string
  workflowState?: WorkflowState
  /** Null when the project has no vendor field, or it's empty on this document. */
  vendorName: string | null
  /** Null when the project has no amount field, or it's empty on this document. */
  amount: string | null
}

/** Whether the project has each field configured at all — used to drop (not dash-out) columns it doesn't have. */
export type ApprovalRowFieldAvailability = {
  vendor: boolean
  amount: boolean
}

// These reads never call more than one page, so the limits have to cover
// the most ids one call can possibly ask about.
// The waiting list back-fills several pages before any of them resolve,
// so that's the worst case.
const MAX_PENDING_IDS = APPROVALS_PAGE_SIZE * WAITING_FILL_MAX_PAGES
// One base-grid row per document, with a vendor and an amount value on it.
const MAX_PENDING_VALUES = MAX_PENDING_IDS * 2

export default function useApprovalRowDetails(documentIds: string[]) {
  const { project } = useProjectContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const [resolved, setResolved] = useState<Map<string, ApprovalRowDetail>>(
    () => new Map(),
  )

  // Sorted so the same set of ids always produces the same cache key.
  const pendingIds = useMemo(
    () =>
      [...new Set(documentIds)].filter((id) => !resolved.has(id)).toSorted(),
    [documentIds, resolved],
  )

  const idFilter = pendingIds.join(',')
  const enabled = pendingIds.length > 0

  const { vendorFieldId, amountFieldId, baseGridId } = useMemo(() => {
    const fields = getFieldsFromProject(project)
    const idForInfoType = (infoTypeId: string) =>
      fields.find((field) => field.info_type_id === infoTypeId)?.id
    return {
      vendorFieldId: idForInfoType(INVOICE_VENDOR_INFO_TYPE_ID),
      amountFieldId: idForInfoType(INVOICE_TOTAL_INFO_TYPE_ID),
      baseGridId: getBaseGrid(project.project_grids || [])?.id,
    }
  }, [project])

  const valueFieldIdsFilter = [vendorFieldId, amountFieldId]
    .filter(Boolean)
    .join(',')

  const { documents, isLoading: documentsLoading } = useGetDocuments({
    // The state ids are left out of a document by default, and the row's state
    // chip is built from them.
    filters: {
      id__in: idFilter,
      limit: `${MAX_PENDING_IDS}`,
      fields__include: 'workflow_states_ids',
    },
    enabled,
  })

  // A field value only names the row it sits on, so these rows are what map a
  // value back to its document.
  const { documentRows, isLoading: rowsLoading } = useGetDocumentRows({
    filters: {
      document_id__in: idFilter,
      project_grid_id: baseGridId,
      limit: `${MAX_PENDING_IDS}`,
    },
    enabled: enabled && !!baseGridId && !!valueFieldIdsFilter,
  })

  const { documentRowValues, isLoading: valuesLoading } =
    useGetDocumentRowValues({
      filters: {
        document_row__document_id__in: idFilter,
        project_grid_field__id__in: valueFieldIdsFilter,
        limit: `${MAX_PENDING_VALUES}`,
      },
      enabled: enabled && !!valueFieldIdsFilter,
    })

  const fetchedDetails = useMemo(() => {
    const documentIdByRowId = new Map(
      documentRows.map((row) => [row.id, row.document_id]),
    )
    const valueByDocumentAndField = new Map<string, string | null>()
    documentRowValues.forEach((value) => {
      const documentId = documentIdByRowId.get(value.document_row_id)
      if (!documentId) return
      const key = `${documentId}:${value.project_grid_field_id}`
      valueByDocumentAndField.set(key, value.final_value)
    })
    const valueFor = (documentId: string, fieldId?: string) =>
      (fieldId && valueByDocumentAndField.get(`${documentId}:${fieldId}`)) ||
      null

    return new Map(
      documents.map((document) => [
        document.id,
        {
          name: document.name,
          workflowState: selectedWorkflow?.workflow_states?.find(({ id }) =>
            document.workflow_states_ids?.includes(id),
          ),
          vendorName: valueFor(document.id, vendorFieldId),
          amount: valueFor(document.id, amountFieldId),
        },
      ]),
    )
  }, [
    amountFieldId,
    documentRowValues,
    documentRows,
    documents,
    selectedWorkflow?.workflow_states,
    vendorFieldId,
  ])

  // Nothing merged below is ever asked for again, so merging while one of the
  // three reads is still in flight would freeze a row with no vendor or amount.
  const settled = !documentsLoading && !rowsLoading && !valuesLoading

  useEffect(() => {
    if (!settled || fetchedDetails.size === 0) return
    setResolved((current) => new Map([...current, ...fetchedDetails]))
  }, [fetchedDetails, settled])

  const availability = useMemo(
    () => ({ vendor: !!vendorFieldId, amount: !!amountFieldId }),
    [amountFieldId, vendorFieldId],
  )

  return { detailsByDocumentId: resolved, availability }
}
